import React, { useRef, useEffect } from "react";
import {
  View,
  Text,
  Modal,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Animated,
  Dimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useTheme } from "../../contexts/ThemeContext";
import { useNavigation } from "@react-navigation/native";

interface AuthRequiredModalProps {
  visible: boolean;
  onClose: () => void;
  featureName: string;
}

const { width } = Dimensions.get("window");
const isSmallMobile = width < 380;

const AuthRequiredModal: React.FC<AuthRequiredModalProps> = ({
  visible,
  onClose,
  featureName,
}) => {
  const { theme } = useTheme();
  const navigation = useNavigation<any>();
  
  // Animation values
  const modalOpacity = useRef(new Animated.Value(0)).current;
  const modalScale = useRef(new Animated.Value(0.9)).current;

  const colors = {
    light: {
      background: "#FFFFFF",
      text: "#18181B",
      subtext: "#71717A",
      border: "#E4E4E7",
      modalOverlay: "rgba(0, 0, 0, 0.5)",
      cardBg: "#FFFFFF",
      benefitBg: "#F9FAFB",
      primaryButton: "#18181B",
      primaryButtonText: "#FFFFFF",
      secondaryButton: "#FFFFFF",
      secondaryButtonText: "#18181B",
      secondaryButtonBorder: "#E4E4E7",
      iconBg: "#F4F4F5",
      gradientStart: "#4F46E5",
      gradientEnd: "#EC4899",
      nonprofitBg: "#EEF2FF",
      nonprofitText: "#4F46E5",
    },
    dark: {
      background: "#18181B",
      text: "#FAFAFA",
      subtext: "#A1A1AA",
      border: "#3F3F46",
      modalOverlay: "rgba(0, 0, 0, 0.8)",
      cardBg: "#27272A",
      benefitBg: "#3F3F46",
      primaryButton: "#FAFAFA",
      primaryButtonText: "#18181B",
      secondaryButton: "#27272A",
      secondaryButtonText: "#FAFAFA",
      secondaryButtonBorder: "#3F3F46",
      iconBg: "#3F3F46",
      gradientStart: "#6366F1",
      gradientEnd: "#EC4899",
      nonprofitBg: "#312E81",
      nonprofitText: "#A5B4FC",
    },
  };

  const c = colors[theme];

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(modalOpacity, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.spring(modalScale, {
          toValue: 1,
          tension: 50,
          friction: 7,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(modalOpacity, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(modalScale, {
          toValue: 0.9,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  const handleSignUp = () => {
    onClose();
    navigation.navigate("SignUp");
  };

  const handleLogIn = () => {
    onClose();
    navigation.navigate("Login");
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
    >
      <View style={[styles.modalOverlay, { backgroundColor: c.modalOverlay }]}>
        <Pressable style={styles.modalBackdrop} onPress={onClose} />
        
        <Animated.View
          style={[
            styles.modal,
            {
              opacity: modalOpacity,
              transform: [{ scale: modalScale }],
              backgroundColor: c.cardBg,
              borderColor: c.border,
            },
          ]}
        >
          {/* Close button */}
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Ionicons name="close" size={20} color={c.text} />
          </TouchableOpacity>

          {/* Account Icon */}
          <View style={[styles.featureIconContainer, { backgroundColor: c.iconBg }]}>
            <LinearGradient
              colors={[c.gradientStart, c.gradientEnd]}
              style={styles.featureIconGradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
            >
              <Ionicons name="person-add" size={32} color="#FFFFFF" />
            </LinearGradient>
          </View>

          {/* Title and Description */}
          <Text style={[styles.title, { color: c.text }]}>
            Account Required
          </Text>
          <Text style={[styles.subtitle, { color: c.subtext }]}>
            Sign up or log in to access {featureName}
          </Text>

          {/* Add padding between description and badge */}
          <View style={{ height: 12 }} />

          {/* Nonprofit Badge */}
          <View style={[styles.nonprofitBadge, { backgroundColor: c.nonprofitBg }]}>
            <Ionicons name="heart" size={16} color={c.nonprofitText} />
            <Text style={[styles.nonprofitText, { color: c.nonprofitText }]}>
              100% Free Nonprofit
            </Text>
          </View>

          {/* Benefits */}
          <View style={styles.benefitsContainer}>
            <Text style={[styles.benefitsTitle, { color: c.text }]}>
              Why we require an account:
            </Text>
            {[
              { icon: "shield-checkmark", text: "Prevent abuse & spam" },
              { icon: "gift", text: "Everything is 100% free" },
              { icon: "people", text: "Join our nonprofit community" },
            ].map((benefit, index) => (
              <View key={index} style={styles.benefitRow}>
                <View style={[styles.benefitIcon, { backgroundColor: c.benefitBg }]}>
                  <Ionicons name={benefit.icon as any} size={16} color={c.gradientStart} />
                </View>
                <Text style={[styles.benefitText, { color: c.subtext }]}>
                  {benefit.text}
                </Text>
              </View>
            ))}
          </View>

          {/* About Text */}
          <Text style={[styles.aboutText, { color: c.subtext }]}>
            bcstem.ca is a nonprofit organization dedicated to providing free opportunities 
            for students. We require an account to maintain quality and prevent abuse.
          </Text>

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.primaryButton, { backgroundColor: c.primaryButton }]}
              onPress={handleSignUp}
              activeOpacity={0.8}
            >
              <Text style={[styles.primaryButtonText, { color: c.primaryButtonText }]}>
                Sign Up - It's Free
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.secondaryButton,
                {
                  backgroundColor: c.secondaryButton,
                  borderColor: c.secondaryButtonBorder,
                },
              ]}
              onPress={handleLogIn}
              activeOpacity={0.8}
            >
              <Text style={[styles.secondaryButtonText, { color: c.secondaryButtonText }]}>
                I Have an Account
              </Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modal: {
    width: isSmallMobile ? width - 32 : Math.min(width - 64, 420),
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 16,
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  },
  featureIconContainer: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    marginBottom: 20,
  },
  featureIconGradient: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
    textAlign: "center",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    fontFamily: "Inter-Regular",
    textAlign: "center",
    marginBottom: 8,
  },
  featureDescription: {
    fontSize: 14,
    fontFamily: "Inter-Regular",
    textAlign: "center",
    marginBottom: 20,
  },
  nonprofitBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    gap: 6,
    marginBottom: 20,
  },
  nonprofitText: {
    fontSize: 13,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  benefitsContainer: {
    marginBottom: 20,
  },
  benefitsTitle: {
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    marginBottom: 12,
  },
  benefitRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
    gap: 12,
  },
  benefitIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  benefitText: {
    fontSize: 14,
    fontFamily: "Inter-Regular",
    flex: 1,
  },
  aboutText: {
    fontSize: 13,
    fontFamily: "Inter-Regular",
    textAlign: "center",
    lineHeight: 18,
    marginBottom: 24,
  },
  buttonContainer: {
    gap: 12,
  },
  primaryButton: {
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  secondaryButton: {
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
});

export default AuthRequiredModal; 